<script>
import {defineComponent, reactive} from "vue";
import 'quill/dist/quill.snow.css';


export default defineComponent({
  components: {},
  props: {
    /**
     * The layout of the info section.
     * imageRight & image left
     */
    layout: {
      type: String,
      default: 'imageLeft',
      validator: (value) => {
        return ['imageLeft', 'imageRight'].includes(value);
      }
    },
    // props used in all layouts
    /**
     * The title in the info section.
     */
    title: {
      type: String,
      default: ''
    },
    /**
     * The subtitle in the info section.
     */
    subtitle: {
      type: String,
      default: ''
    },
    /**
     * The body text in the info section.
     * Either a string or an array of strings.
     * Each string will be its own paragraph.
     */
    bodyText: {
      type: [String, Array],
      default: ""
    },
    /**
     * Will hide the image if true.
     */
    hideImage: {
      type: Boolean,
      default: false
    },
    /**
     * The src attribute for the image.
     */
    imageSrc: {
      type: String,
      default: ''
    },
    /**
     * The alt attribute for the image.
     */
    imageAlt: {
      type: String,
      default: ''
    },
    /**
     * The border radius of the article image.
     */
    imageBorderRadius: {
      type: String,
      default: "8px" // $radius-rounded
    },
    /**
     * Background color of info section.
     * Can be any valid css color syntax.
     */
    backgroundColor: {
      type: String,
      default: '#FFFFFF'
    },
    /**
     * Color of text in info section.
     * Can be any valid css color syntax.
     */
    textColor: {
      type: String,
      default: '#000000'
    },
  },
  setup(props) {
    props = reactive(props);
    return {
      classes: {
        'info-section-content-container': true,
        [`layout--${props.layout || 'imageLeft'}`]: true,
      }
    };
  },
  data() {
    return {};
  },
  computed: {
    cssProps() {
      return {
        '--background-color': this.backgroundColor,
        '--image-border-radius': this.imageBorderRadius,
        '--text-color': this.textColor,
      };
    },
  },
  watch: {},
  mounted() {
  },
  methods: {}
});

</script>

<template>
  <div class="outer-container">
    <div
      id="landing-info-section"
      class="inner-container"
      :style="cssProps"
    >
      <div>
        <div :class="classes">
          <div class="text-container">
            <img
              v-if="imageSrc && !hideImage"
              :src="imageSrc"
              :alt="imageAlt"
              onload="this.style.opacity=1"
            >
            <h1 class="title-text">
              {{ title }}
            </h1>
            <p
              v-if="subtitle"
              class="info-section-subtitle subtitle-regular"
            >
              {{ subtitle }}
            </p>
            <div
              v-if="bodyText"
              class="ql-snow"
            >
              <div
                class="ql-editor info-section-body-text-container body-1-regular"
                v-html="bodyText"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

#landing-info-section {
  background-color: var(--background-color);

  // basic layout
  .info-section-content-container {

    justify-content: space-between;
    align-items: flex-start;
    height: 100%;
    width: 100%;
    gap: $space-2xl;
    padding: $space-md;


    .text-container {
      //@apply w-full flex flex-col h-full text-lg;

      color: var(--text-color);

      .info-section-subtitle {
        margin-top: $space-sm;
      }

      .info-section-body-text-container {
        margin-top: $space-xl;

        p:not(:first-child) {
          margin-top: $space-lg;
        }
      }
    }

    img {
      margin-bottom: $space-xl;
      display: inline-block;
      border-radius: var(--image-border-radius);
      height: auto;
      width: 33%;
      flex-shrink: 0;
      opacity: 0;
      transition: all .4s ease;
      -webkit-transition: all .4s ease;
      -moz-transition: all .4s ease;
      -ms-transition: all .4s ease;
      -o-transition: all .4s ease;
    }

    &.layout--imageLeft {
      flex-direction: row-reverse;

      img {
        float: left;
        margin-right: $space-xl;
      }
    }

    &.layout--imageRight {
      flex-direction: row;

      img {
        float: right;
        margin-left: $space-xl;
      }
    }

    @media screen and (max-width: $breakpoint-max-lg) {
      &.info-section-content-container {
        flex-direction: column;

        &.image-position--left {
          flex-direction: column-reverse;
        }
      }
    }

    // Mobile
    @media screen and (max-width: $breakpoint-max-sm) {
      flex-direction: column;

      img {
        width: 100% !important;
        margin: $space-md 0 !important;
        float: none !important;
        display: block;
      }

      .text-container {
        display: flex;
        flex-direction: column;

        h2 {
          order: -2;
        }

        img {
          order: -1;
        }

        .info-section-subtitle,
        .info-section-body-text-container {
          order: 0;
        }
      }
    }

    // Container responsiveness - only for preview mode on builder!

    //max-lg
    @container (min-width: 768px) and (max-width: 1023px) {
      &.info-section-content-container {
        flex-direction: column;

        &.image-position--left {
          flex-direction: column-reverse;
        }
      }
    }

    //max-sm
    @container (min-width: 0px) and (max-width: 639px) {
      flex-direction: column;

      img {
        width: 100% !important;
        margin: $space-md 0 !important;
        float: none !important;
        display: block;
      }

      .text-container {
        display: flex;
        flex-direction: column;

        h2 {
          order: -2;
        }

        img {
          order: -1;
        }

        .info-section-subtitle,
        .info-section-body-text-container {
          order: 0;
        }
      }
    }

    .title-text {
      font-size: $section-heading-size;
      font-weight: $section-heading-weight;
      line-height: $section-heading-line;
    }

    p {
      word-break: break-word;
    }

    ::v-deep .ql-editor blockquote {
      background: transparent !important;
      border-left: none !important;
      margin: 0 !important;
      padding: 0 !important;
    }

    ::v-deep .ql-editor blockquote p {
      display: inline !important;
      margin: 0 !important;
    }

    ::v-deep .ql-editor blockquote::before {
      content: '“';
      font-size: 1.2em;
      vertical-align: text-bottom;
      margin-right: 0.25em;
      color: var(--text-color);
    }

    ::v-deep .ql-editor blockquote::after {
      content: '”';
      font-size: 1.2em;
      vertical-align: text-bottom;
      margin-left: 0.25em;
      color: var(--text-color);
    }
  }
}
</style>
